import { Download, FileWarning, Loader, Trash2 } from "lucide-react";
import { useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import { Button } from "@/components/button";
import { Checkbox } from "@/components/checkbox";
import { FILE_ICONS } from "@/features/my-files/assets/file-icons";
import { useDownloadFile } from "@/features/my-files/hooks/useDownloadFile";
import { getExtensionFromFilename } from "@/utils/getExtensionFromMime.ts";
import { convertUTCtoEastern } from "@/utils/timezoneConverter.ts";

import type { FileAsset } from "../types.ts";

import { DeleteAssetModal } from "./DeleteAssetModal.tsx";

function handleDownload(file: File) {
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");

  a.href = url;
  a.download = file.name;
  document.body.append(a);
  a.click();
  URL.revokeObjectURL(url);
  a.remove();
}

type UploadedAssetProps = {
  file: FileAsset;
  handleCheckboxChange: (attachmentId: number, isSelected: boolean) => void;
  isSelected: boolean;
  onAssetClick?: (assetId: string) => void;
  onDelete: (attachmentId: number) => void;
};

type UploadedAssetsButtonsProps = {
  file: FileAsset;
  onDelete: (attachmentId: number) => void;
};

function UploadingButtons() {
  return (
    <Button color="gray" isIconOnly size="sm" variant="tertiary">
      <Loader className="animate-spin text-border-brand-solid" />
    </Button>
  );
}

function SuccessButtons({ file, onDelete }: UploadedAssetsButtonsProps) {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { mutate: downloadFile } = useDownloadFile();

  return (
    <>
      <DeleteAssetModal
        assetName={file.file.name}
        isOpen={isDeleteModalOpen}
        onDelete={() => {
          onDelete(file.attachmentId as number);
          setIsDeleteModalOpen(false);
        }}
        onOpenChange={setIsDeleteModalOpen}
      />
      <Button
        color="gray"
        isIconOnly
        onPress={() => setIsDeleteModalOpen(true)}
        size="sm"
        variant="tertiary"
      >
        <Trash2 className="text-button-tertiary-fg" />
      </Button>
      <Button
        color="gray"
        isIconOnly
        onPress={() => {
          if (file.attachmentId) {
            downloadFile(file.attachmentId);
          } else if (file.awsDownloadUrl) {
            window.open(file.awsDownloadUrl, "_blank");
          } else {
            handleDownload(file.file);
          }
        }}
        size="sm"
        variant="tertiary"
      >
        <Download className="text-button-tertiary-color-fg" />
      </Button>
    </>
  );
}

function ErrorButtons({ file, onDelete }: UploadedAssetsButtonsProps) {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  return (
    <>
      <DeleteAssetModal
        assetName={file.file.name}
        isOpen={isDeleteModalOpen}
        onDelete={() => {
          onDelete(file.attachmentId as number);
          setIsDeleteModalOpen(false);
        }}
        onOpenChange={setIsDeleteModalOpen}
      />
      <Button
        color="gray"
        isIconOnly
        onPress={() => setIsDeleteModalOpen(true)}
        size="sm"
        variant="tertiary"
      >
        <Trash2 className="text-button-tertiary-fg" />
      </Button>
      <Button
        color="gray"
        isDisabled
        isIconOnly
        onPress={() => handleDownload(file.file)}
        size="sm"
        variant="tertiary"
      >
        <FileWarning className="text-text-error-primary" />
      </Button>
    </>
  );
}

export function UploadedAsset({
  file,
  handleCheckboxChange,
  isSelected = false,
  onAssetClick,
  onDelete,
}: UploadedAssetProps) {
  const checkboxRef = useRef<HTMLInputElement>(null);

  const handleClick = (e: React.MouseEvent) => {
    if (
      e.target instanceof HTMLElement &&
      (e.target.closest("button") || e.target.closest('input[type="checkbox"]'))
    ) {
      return;
    }

    if (file.status === "success" && onAssetClick) {
      e.stopPropagation();
      onAssetClick(file.id);
    } else {
      e.stopPropagation();
    }
  };

  return (
    <div
      className={twMerge(
        "flex items-center justify-between gap-xs rounded-xl border border-border-secondary bg-bg-primary px-xl py-md",
        isSelected
          ? "border-border-brand bg-bg-brand-primary"
          : "hover:bg-bg-primary-hover hover:border-button-secondary-border-hover",
        file.status === "success" && "cursor-pointer",
      )}
      onClick={handleClick}
    >
      <div className="flex items-center gap-md overflow-hidden">
        {file.status === "success" && (
          <Checkbox
            className="text-text-secondary"
            inputRef={checkboxRef}
            onChange={(e) => handleCheckboxChange(file.attachmentId!, e)}
          />
        )}
        <FileIcon extension={getExtensionFromFilename(file.file.name)} />
        <div className="flex flex-col overflow-hidden">
          <span
            className="truncate text-sm font-medium text-text-secondary"
            title={file.file.name}
          >
            {file.file.name}
          </span>
          {file.createdAt && (
            <span className="text-xs text-text-tertiary">
              Added {convertUTCtoEastern(file.createdAt, "MMM D, YYYY")}
            </span>
          )}
        </div>
      </div>
      <div className="flex shrink-0 gap-xs">
        {file.status === "uploading" && <UploadingButtons />}
        {file.status === "success" && (
          <SuccessButtons file={file} onDelete={onDelete} />
        )}
        {file.status === "error" && (
          <ErrorButtons file={file} onDelete={onDelete} />
        )}
      </div>
    </div>
  );
}

export function FileIcon({
  extension,
}: {
  extension: boolean | null | string;
}) {
  if (typeof extension === "string" && extension in FILE_ICONS) {
    const IconComponent = FILE_ICONS[extension as keyof typeof FILE_ICONS];

    return <IconComponent className="size-xl shrink-0" />;
  } else {
    const IconComponent = FILE_ICONS.fallback;

    return <IconComponent className="size-xl shrink-0" />;
  }
}
